import { SubscriptionStatus } from '@neogeniux/api-contracts';
import { AccessDeniedScreen } from '@app/shared/components/access-denied/access-denied-screen';
import { FullPageLoader } from '@app/shared/components/full-page-loader';
import {
  NOT_INCLUDED_PATH,
  PRIVATE_PATH,
  PUBLIC_PATH,
} from '@app/shared/constants/paths.constant';
import { useAuthHydrated } from '@app/shared/hooks/use-auth-hydrated';
import { useContractStatus } from '@app/shared/hooks/contract.hook';
import { useMyBilling } from '@app/shared/hooks/self-billing.hook';
import { useBusinessContextReady } from '@app/shared/hooks/use-business-context-ready';
import { useAuthStore } from '@app/shared/store/auth.store';
import { useBranchStore } from '@app/shared/store/branch.store';
import { useMenuStore } from '@app/shared/store/menu.store';
import { getMenuByUserService } from '@app/shared/services/menu.service';
import { resolveAuthRedirect } from '@app/shared/utils/auth-redirect.utils';
import { i18n } from '@i18n/translations';
import type { ContractStatusResponse } from '@neogeniux/api-contracts';
import { useQueryClient } from '@tanstack/react-query';
import { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

/** Routes a `suspended` user is still allowed to visit — pay flow,
 *  contract signing (in case it's also pending), profile (to
 *  update email/phone), and /account so they can see what they owe. */
const SUSPENDED_ALLOW_PREFIXES = [
  '/account',
  PRIVATE_PATH.CONTRACT_SIGNING,
  PRIVATE_PATH.PROFILE,
];

const SADMIN_CONTRACT_BYPASS_KEY = 'sadmin_contract_bypass';

interface AuthGuardProps {
  children: ReactNode;
}

export const AuthGuard: FC<AuthGuardProps> = ({ children }) => {
  const hasHydrated = useAuthHydrated();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const businessContextReady = useBusinessContextReady();
  const menuItems = useMenuStore((state) => state.menu);
  const setMenu = useMenuStore((state) => state.setMenu);
  const selectedBranchOfficeId = useBranchStore(
    (state) => state.selectedBranchOfficeId
  );
  const queryClient = useQueryClient();

  // Global per-branch access refresh. Role permissions are per-branch, so
  // switching the active branch — via the selector OR any other path — must
  // re-evaluate the operator's access: page gates refetch automatically (the
  // `my-permissions` query is branch-keyed), and the sidebar menu is refreshed
  // here (it's a Zustand store set once at login, not a branch-keyed query).
  // Lives on the always-mounted AuthGuard so EVERY branch change is covered,
  // not just the one in the branch-selector handler. Skips the first run
  // (login already seeded the menu); fires only on an actual change.
  const prevBranchRef = useRef<string | null>(null);
  const branchInitRef = useRef(false);
  useEffect(() => {
    if (!isAuthenticated) return;
    const branch = selectedBranchOfficeId ?? null;
    if (!branchInitRef.current) {
      branchInitRef.current = true;
      prevBranchRef.current = branch;
      return;
    }
    if (prevBranchRef.current === branch) return;
    prevBranchRef.current = branch;
    getMenuByUserService()
      .then(setMenu)
      .catch(() => undefined);
  }, [selectedBranchOfficeId, isAuthenticated, setMenu]);
  const bypassed = sessionStorage.getItem(SADMIN_CONTRACT_BYPASS_KEY) === '1';

  // Read the seeded snapshot (populated by `seedContractStatus` after
  // login). `getQueryData` is a synchronous read — it never triggers
  // a fetch. When present + `needsSigning=false`, the gate is closed
  // and we do NOT enable `useContractStatus` — zero network hop for
  // the 99% case where the operator has already signed.
  //
  // Fetch only when:
  //  • cache is empty (page reload, session restore, first ever load), OR
  //  • cache says `needsSigning=true` (kept fresh so re-signing after a
  //    contract-version bump is picked up on the next stale check).
  const cachedContractStatus = queryClient.getQueryData<ContractStatusResponse>(
    ['contracts', 'status']
  );
  const shouldFetchContract =
    isAuthenticated &&
    businessContextReady &&
    !bypassed &&
    (!cachedContractStatus || cachedContractStatus.needsSigning);
  const {
    data: fetchedContractStatus,
    isPending: isContractStatusPending,
    isFetched: isContractStatusFetched,
  } = useContractStatus(shouldFetchContract);
  const contractStatus = fetchedContractStatus ?? cachedContractStatus;
  const { data: billing } = useMyBilling();

  const location = useLocation();
  const [requestedLocation, setRequestedLocation] = useState<string | null>(
    null
  );

  // Persist rehydration (Zustand v5) is async — first render sees
  // the default state and would kick a valid session to /auth/login.
  // Wait until the store has settled before deciding.
  if (!hasHydrated) {
    return <FullPageLoader />;
  }

  if (!isAuthenticated) {
    if (location.pathname !== requestedLocation) {
      setRequestedLocation(location.pathname);
    }

    return <Navigate to={PUBLIC_PATH.LOGIN} />;
  }

  // Business-context gate — block the private tree until auth,
  // business, branch, and role/permissions all resolve. Prevents
  // the wave-1 request storm during login + impersonation where
  // dashboard hooks fire with an empty branch, then refire when
  // the store settles. Without this, an impersonated tenant kicks
  // 50+ requests where 20 would do — see docs/rules/005-performance
  // .md + the incident notes on SOL CARIBEÑO impersonation.
  //
  // Contract fetch, billing fetch, menu check, and route matching
  // below all depend on the branch header being live, so they run
  // AFTER this gate closes.
  if (!businessContextReady) {
    return <FullPageLoader />;
  }

  // Contract gate loading — block the private tree until we know
  // whether the operator needs to sign. Without this the app renders
  // for a beat then bounces to `/contract-signing` mid-navigation
  // (visible UI flash). Only fires when we're actually fetching AND
  // no cached snapshot exists — the seeded/needsSigning=false path
  // skips this branch entirely (no fetch, gate closed immediately).
  const contractRouteEarly = location.pathname.startsWith(
    PRIVATE_PATH.CONTRACT_SIGNING
  );
  if (
    shouldFetchContract &&
    !contractRouteEarly &&
    !cachedContractStatus &&
    (isContractStatusPending || !isContractStatusFetched)
  ) {
    return <FullPageLoader subtitle={i18n.COMMON.VALIDATING_CONTRACT} />;
  }

  // Contract gate — redirect to standalone signing page BEFORE any private
  // route renders. Sadmin impersonation can bypass for the current session
  // via the sign-page bypass button.
  const needsSigning =
    !!contractStatus?.needsSigning && !!contractStatus?.currentVersion;
  const onContractRoute = location.pathname.startsWith(
    PRIVATE_PATH.CONTRACT_SIGNING
  );
  if (needsSigning && !bypassed && !onContractRoute) {
    return <Navigate to={PRIVATE_PATH.CONTRACT_SIGNING} replace />;
  }
  // If user is on the contract page but no longer needs signing → bounce out.
  // Resolve destination against the loaded menu so operators without
  // `dashboard.view` (cashier / kitchen / any role starting at another
  // surface) land on their first accessible route instead of the
  // AccessDeniedScreen on /dashboard.
  if (onContractRoute && (!needsSigning || bypassed)) {
    const user = useAuthStore.getState().user;
    const target = resolveAuthRedirect(user, menuItems);
    return <Navigate to={target} replace />;
  }

  // Suspended-subscription lockscreen — redirect every private
  // route except the recovery surfaces (/account, /profile,
  // /contract-signing). Pay flow + sign-out remain reachable so
  // the operator can self-recover. /auth/* lives outside this
  // guard.
  //
  // Target = `/account` (not `/account/billing`) because the account
  // overview renders the full "Métodos de Pago" section with the
  // Stripe enrollment button. `/account/billing` shows only the
  // payment history + lifecycle alert; landing there leaves the
  // operator stuck with the banner and no card-add path.
  if (billing?.subscriptionStatus === SubscriptionStatus.SUSPENDED) {
    const isOnRecoverySurface = SUSPENDED_ALLOW_PREFIXES.some((prefix) =>
      location.pathname.startsWith(prefix)
    );
    if (!isOnRecoverySurface) {
      return <Navigate to={PRIVATE_PATH.ACCOUNT} replace />;
    }
  }

  // If user has a token with lower privileges and tries to access a route with higher privileges, then we redirect to the login page.
  // and login will redirect the user to the correct route.
  if (menuItems.length && !onContractRoute) {
    const isRouteAllowed = menuItems.some(
      (item) =>
        // Check if the current path starts with the menu item link (allowing all descendants)
        (item.link && location.pathname.startsWith(item.link)) ||
        // Check if the current path starts with any child link (allowing all descendants)
        item.children?.some(
          (child) => child.link && location.pathname.startsWith(child.link)
        ) ||
        // Check if the path is in the NOT_INCLUDED_PATH list
        NOT_INCLUDED_PATH.some((path) => {
          return location.pathname.includes(
            typeof path === 'string' ? path : ''
          );
        })
    );

    if (!isRouteAllowed) {
      // Authenticated user without permission to this route. Previous
      // behavior bounced to the login page which was confusing — they
      // weren't logged out, they just lacked the perm. Render the
      // friendly access-denied screen with a "Ir al inicio" CTA
      // instead so the operator understands what happened and
      // can recover without re-authenticating.
      return <AccessDeniedScreen />;
    }
  }

  // This is done so that in case the route changes by any chance through other
  // means between the moment of request and the render we navigate to the initially
  // requested route.
  if (requestedLocation && location.pathname !== requestedLocation) {
    setRequestedLocation(null);
    return <Navigate to={requestedLocation} />;
  }

  return <>{children}</>;
};

export default AuthGuard;
